import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, output, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { SelectComponent, SelectOption } from './ui/select/select.component';

@Component({
  selector: 'app-device-selector',
  imports: [SelectComponent, FormsModule, MatIconModule, MatTooltipModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (devices().length > 0) {
      <div class="device-selector-field text-left flex items-center gap-2 w-full min-w-0">
        @if (!hideIcon()) {
          <mat-icon [matTooltip]="label() | translate" class="flex items-center justify-center text-gray-500 shrink-0">
            {{ icon() }}
          </mat-icon>
        }
        <ui-select
          class="flex-grow min-w-0"
          [options]="selectOptions()"
          [ngModel]="selectedDeviceId()"
          (valueChange)="onSelectionChange($any($event))"
          [disabled]="isDisabled()"
          [compact]="true"
        ></ui-select>
      </div>
    } @else {
      <div class="no-devices-found">
        <mat-icon [matTooltip]="label() | translate" class="w-4! h-4! text-base! flex items-center justify-center text-gray-400 cursor-help">
          {{ icon() }}
        </mat-icon>
        <span>{{ 'DeviceSelector.NoDeviceFound' | translate: { deviceType: translatedDeviceType() } }}</span>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .device-selector-field {
        width: 100%;
      }
      ui-select {
        flex-grow: 1;
        width: 100%;
        min-width: 0;
        :host::ng-deep .skz-select-trigger.compact {
          display: flex;
          width: 100%;
        }
      }
      .no-devices-found {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 0;
        font-style: italic;
        opacity: 0.7;
        font-size: 0.8125rem;
      }
    `,
  ],
})
export class DeviceSelectorComponent implements OnDestroy {
  kind = input.required<'audioinput' | 'videoinput' | 'audiooutput'>();
  label = input.required<string>();
  isDisabled = input<boolean>(false);
  selectionChange = output<string>();

  devices: WritableSignal<MediaDeviceInfo[]> = signal([]);
  selectedDeviceId: WritableSignal<string | null> = signal(null);

  private permissionService = inject(MediaDevicePermissionWatcherService);
  private translate = inject(TranslateService);
  private permissionSubscription: Subscription;

  hideIcon = input<boolean>(false);
  icon = computed(() => {
    if (this.kind() === 'audioinput') return 'settings_voice';
    if (this.kind() === 'audiooutput') return 'volume_up';
    return 'videocam';
  });
  translatedDeviceType = computed(() => {
    if (this.kind() === 'audioinput') return this.translate.instant('DeviceSelector.Microphone');
    if (this.kind() === 'audiooutput') return this.translate.instant('DeviceSelector.Speaker');
    return this.translate.instant('DeviceSelector.Camera');
  });

  private cleanLabel(label: string): string {
    if (!label) return '';
    let clean = label.replace(/\s*\([^)]*\)/g, '');
    clean = clean.replace(/\bbuilt-in\b/gi, '');
    clean = clean.replace(/\bdevice\b/gi, '');
    return clean.replace(/\s+/g, ' ').trim();
  }

  selectOptions = computed<SelectOption[]>(() => {
    return this.devices().map((device, index) => ({
      value: device.deviceId,
      label: this.cleanLabel(device.label) || `${this.translate.instant('DeviceSelector.UnknownDevice')} ${index + 1}`,
    }));
  });

  constructor() {
    this.permissionSubscription = this.permissionService.permissionsRefreshed$.subscribe(() => {
      this.fetchDevices();
    });
  }

  ngOnDestroy(): void {
    this.permissionSubscription.unsubscribe();
  }

  private async fetchDevices(): Promise<void> {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const filteredDevices = allDevices.filter((device) => device.kind === this.kind());
      this.devices.set(filteredDevices);

      this.initializeSelection(filteredDevices);
    } catch (error) {
      console.error('Error fetching media devices:', error);
      this.devices.set([]);
    }
  }

  private initializeSelection(availableDevices: MediaDeviceInfo[]): void {
    if (availableDevices.length === 0) {
      this.selectedDeviceId.set(null);
      return;
    }

    // Check localStorage for a previously saved device for this kind.
    let storageKey: string;
    if (this.kind() === 'audioinput') {
      storageKey = 'selectedMicrophone';
    } else if (this.kind() === 'audiooutput') {
      storageKey = 'selectedSpeaker';
    } else {
      storageKey = 'selectedCamera';
    }
    const savedDeviceId = localStorage.getItem(storageKey);

    const deviceExists = availableDevices.some((device) => device.deviceId === savedDeviceId);

    if (savedDeviceId && deviceExists) {
      this.selectedDeviceId.set(savedDeviceId);
    } else {
      const firstDeviceId = availableDevices[0].deviceId;
      this.selectedDeviceId.set(firstDeviceId);
      localStorage.setItem(storageKey, firstDeviceId);
    }

    this.selectionChange.emit(this.selectedDeviceId());
  }

  onSelectionChange(deviceId: string): void {
    this.selectedDeviceId.set(deviceId);
    let storageKey: string;
    if (this.kind() === 'audioinput') {
      storageKey = 'selectedMicrophone';
    } else if (this.kind() === 'audiooutput') {
      storageKey = 'selectedSpeaker';
    } else {
      storageKey = 'selectedCamera';
    }
    localStorage.setItem(storageKey, deviceId);
    this.selectionChange.emit(deviceId);
  }
}
